import { Link, useNavigate } from "react-router-dom";
import { Plus, Camera, Flame, History, Trophy, TrendingUp, Handshake, Share2, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import { cn, formatCurrency } from "@/lib/utils";
import type { DashboardViewProps } from "@/ui/view-props";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { CountUp } from "@/components/ui/count-up";
import { SkeletonDashboard } from "./SkeletonLoader";

export function DashboardNeonView(p: DashboardViewProps) {
  const navigate = useNavigate();
  const targetPrefix = p.isReadOnly ? `/public/${p.targetUserId}` : "";

  const copyLiveLink = () => {
    const url = `${window.location.origin}/live/${p.activeSessionId}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Live link copied! Share it with spectators to show the live scoreboard.');
    });
  };

  const handlePlayerClick = (playerId: string) => {
    const path = p.isReadOnly 
      ? `/public/${p.targetUserId}/player/${playerId}` 
      : `/player/${playerId}`;
    navigate(path);
  };

  if (p.loading) {
    return (
      <div className="neon-main">
        <SkeletonDashboard />
      </div>
    );
  }

  // Extract top 3 for podium
  const topThree = p.unsettledStats.slice(0, 3);
  const remainingPlayers = p.unsettledStats.slice(3);

  return (
    <div className="animate-fade-in flex flex-col gap-8">

      {p.activeSessionId && !p.isReadOnly && (
        <div className="glass-card p-5 border-neon-amber/50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="animate-pulse-glow w-10 h-10 rounded-full bg-neon-amber/20 flex items-center justify-center shrink-0">
                <Flame className="w-5 h-5 text-neon-amber" />
              </div>
              <div>
                <div className="font-extrabold text-base text-white">Active Session</div>
                <div className="text-xs text-neon-muted">Session in progress</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="neon-btn-ghost flex items-center gap-1.5 py-1.5 px-3 text-xs"
                title="Copy live session link for players to monitor"
                onClick={copyLiveLink}
              >
                <Share2 className="w-3.5 h-3.5" /> Live Link
              </button>
              <button
                type="button"
                className="neon-btn-ghost py-1.5 px-3 text-xs text-neon-crimson border-neon-crimson/40 hover:bg-neon-crimson/10"
                onClick={p.onAbortSession}
              >
                Abort
              </button>
              <Link to="/session/new">
                <button type="button" className="neon-btn-primary py-2 px-5 text-sm">Resume</button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Grid ──────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">
        {!p.isReadOnly && (
          <>
            <Link to="/session/new" className="no-underline">
              <button type="button" className="neon-btn-primary w-full h-[100px] flex flex-col items-center justify-center gap-2 rounded-2xl">
                <Plus className="w-6 h-6" />
                <span className="text-xs font-bold uppercase tracking-widest">New Session</span>
              </button>
            </Link>
            <Link to="/session/import" className="no-underline">
              <button type="button" className="neon-btn-secondary w-full h-[100px] flex flex-col items-center justify-center gap-2 rounded-2xl bg-transparent border-neon-border">
                <Camera className="w-6 h-6" />
                <span className="text-xs font-bold uppercase tracking-widest">Import</span>
              </button>
            </Link>
          </>
        )}
        <Link to={`${targetPrefix}/sessions`} className={cn("no-underline", p.isReadOnly && "col-span-2")}>
          <button type="button" className="neon-btn-secondary w-full h-[100px] flex flex-col items-center justify-center gap-2 rounded-2xl bg-transparent border-neon-border">
            <History className="w-6 h-6" />
            <span className="text-xs font-bold uppercase tracking-widest">Sessions</span>
          </button>
        </Link>
        {!p.isReadOnly && (
          <Link to="/settlement" className="no-underline">
            <button type="button" className="neon-btn-secondary w-full h-[100px] flex flex-col items-center justify-center gap-2 rounded-2xl bg-transparent border-neon-border">
              <Handshake className="w-6 h-6" />
              <span className="text-xs font-bold uppercase tracking-widest">Settle Up</span>
            </button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* ── Unsettled Standings (Podium) ────────────────── */}
        {p.unsettledStats.length > 0 && (
          <div className="animate-slide-up delay-75 flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-neon-cyan" />
                <h2 className="text-sm font-black text-white uppercase tracking-wider">
                  Current Standings
                </h2>
              </div>
              <Link to={`${targetPrefix}/who-pays-who`} className="text-[10px] font-bold text-neon-cyan hover:underline">
                WHO PAYS WHO →
              </Link>
            </div>

            {/* Podium Display */}
            <div className="grid grid-cols-3 items-end gap-2 pt-6 pb-2">
              {/* 2nd Place */}
              {topThree[1] ? (
                <div 
                  onClick={() => handlePlayerClick(topThree[1].id)}
                  className="podium-card podium-silver cursor-pointer h-[140px] animate-slide-up delay-150"
                >
                  <PlayerAvatar name={topThree[1].name} avatarUrl={topThree[1].avatar_url} size={40} className="mb-2" />
                  <div className="text-[10px] font-bold truncate w-full px-1">{topThree[1].name}</div>
                  <div className="text-xs font-black text-white">{formatCurrency(topThree[1].totalNet)}</div>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-400 text-black text-[10px] font-black px-1.5 py-0.5 rounded-full">2ND</div>
                </div>
              ) : <div />}
              
              {/* 1st Place */}
              {topThree[0] ? (
                <div 
                  onClick={() => handlePlayerClick(topThree[0].id)}
                  className="podium-card podium-winner cursor-pointer h-[160px] animate-slide-up"
                >
                  <PlayerAvatar name={topThree[0].name} avatarUrl={topThree[0].avatar_url} size={48} className="mb-2" />
                  <div className="text-[10px] font-bold truncate w-full px-1">{topThree[0].name}</div>
                  <div className="text-sm font-black text-white">{formatCurrency(topThree[0].totalNet)}</div>
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full z-20">1ST</div>
                </div>
              ) : <div />}

              {/* 3rd Place */}
              {topThree[2] ? (
                <div 
                  onClick={() => handlePlayerClick(topThree[2].id)}
                  className="podium-card podium-bronze cursor-pointer h-[120px] animate-slide-up delay-225"
                >
                  <PlayerAvatar name={topThree[2].name} avatarUrl={topThree[2].avatar_url} size={36} className="mb-2" />
                  <div className="text-[10px] font-bold truncate w-full px-1">{topThree[2].name}</div>
                  <div className="text-xs font-black text-white">{formatCurrency(topThree[2].totalNet)}</div>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-700 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">3RD</div>
                </div>
              ) : <div />}
            </div>

            {/* Remaining Players List */}
            {remainingPlayers.length > 0 && (
              <div className="glass-card overflow-hidden">
                {remainingPlayers.map((player, i) => (
                  <div
                    key={player.id}
                    onClick={() => handlePlayerClick(player.id)}
                    className="flex items-center justify-between p-3 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-bold text-neon-muted w-4">{i + 4}</span>
                      <PlayerAvatar name={player.name} avatarUrl={player.avatar_url} size={28} />
                      <div className="text-xs font-bold">{player.name}</div>
                    </div>
                    <div className={cn(
                      "text-sm font-black",
                      player.totalNet > 0 ? "text-neon-green" : player.totalNet < 0 ? "text-neon-crimson" : "text-neon-muted"
                    )}>
                      {player.totalNet > 0 ? "+" : ""}{formatCurrency(player.totalNet)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Unsettled Expenses ──────────────────────────── */}
        {p.unsettledExpenses.length > 0 && (
          <div className="animate-slide-up delay-150 flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-neon-green" />
                <h2 className="text-sm font-black text-white uppercase tracking-wider">
                  Expenses
                </h2>
              </div>
              <Link to={`${targetPrefix}/expenses`} className="text-[10px] font-bold text-neon-green hover:underline">
                ALL →
              </Link>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="flex flex-col">
                {p.unsettledExpenses.map((exp) => (
                  <div
                    key={exp.id}
                    className="flex items-center justify-between p-4 border-b border-white/5 last:border-0"
                  >
                    <div>
                      <div className="text-xs font-bold text-white">{exp.description || 'Expense'}</div>
                      <div className="text-[10px] text-neon-muted">Paid by {exp.payer?.name}</div>
                    </div>
                    <div className="text-sm font-black text-neon-crimson">
                      {formatCurrency(exp.amount)}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between p-4 bg-neon-amber/10">
                  <div className="text-[10px] font-black uppercase tracking-tighter">TOTAL UNSETTLED</div>
                  <div className="text-sm font-black text-white">
                    {formatCurrency(p.unsettledExpenses.reduce((sum, e) => sum + Number(e.amount), 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Lifetime Standings ──────────────────────────── */}
        {p.allTimeStats.length > 0 && (
          <div className="animate-slide-up delay-150 flex flex-col gap-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-neon-cyan" />
                <h2 className="text-sm font-black text-white uppercase tracking-wider">
                  Lifetime Leaderboard
                </h2>
              </div>
              <Link to={`${targetPrefix}/statistics`} className="text-[10px] font-bold text-neon-cyan hover:underline">
                FULL STATS →
              </Link>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="flex flex-col">
                {p.allTimeStats.slice(0, 5).map((player, i) => (
                  <div
                    key={player.id}
                    onClick={() => handlePlayerClick(player.id)}
                    className="flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-bold text-neon-muted w-4">#{i + 1}</span>
                      <PlayerAvatar
                        name={player.name}
                        avatarUrl={player.avatar_url}
                        size={32}
                      />
                      <span className="text-xs font-bold text-white">{player.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={cn(
                          "text-sm font-black",
                          player.totalNet > 0 ? "text-neon-green" : player.totalNet < 0 ? "text-neon-crimson" : "text-neon-muted"
                        )}>
                          {player.totalNet > 0 ? "+" : ""}{formatCurrency(player.totalNet)}
                        </div>
                        <div className="text-[10px] text-neon-muted font-bold">
                          {player.totalSessions} SESS
                        </div>
                      </div>
                      <ArrowUpRight className="w-3 h-3 text-neon-muted" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Recent settled session ───────────────────────── */}
        {p.settings.recent_results && p.recentSettledSession && (
          <div className="animate-slide-up delay-225 flex flex-col gap-4">
            <div className="text-sm font-black text-white uppercase tracking-wider px-1">
              Last Settled Session
            </div>
            <Link to={`${targetPrefix}/session/${p.recentSettledSession.id}`} className="no-underline">
              <div className="glass-card p-5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-black text-white">{format(new Date(p.recentSettledSession.date), "MMMM d, yyyy")}</div>
                  <div className="text-xs text-neon-muted mt-1">{p.recentSettledSession.players.length} players joined</div>
                </div>
                <div className="px-3 py-1.5 rounded-full bg-neon-green/10 border border-neon-green/30 text-neon-green text-[10px] font-black uppercase tracking-wider">
                  Settled
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* ── Who's hot ────────────────────────────────────── */}
        {p.settings.whos_hot && p.hotPlayers.length > 0 && (
          <div className="animate-slide-up delay-300 flex flex-col gap-4">
            <div className="flex items-center gap-2 px-1">
              <Flame className="w-5 h-5 text-neon-orange" />
              <span className="text-sm font-black text-white uppercase tracking-wider">On Fire</span>
            </div>
            <div className="glass-card p-2 flex flex-col gap-2">
              {p.hotPlayers.slice(0, 3).map((h, i) => (
                <div
                  key={h.name}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-xl",
                    i === 0 ? "bg-neon-orange/10 border border-neon-orange/20 animate-pulse-subtle" : "bg-white/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    {i === 0 && <Flame className="w-4 h-4 text-neon-orange fill-neon-orange" />}
                    <span className="text-xs font-bold text-white">{h.name}</span>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-neon-orange/20 border border-neon-orange/40 text-[10px] font-black text-neon-orange flex items-center gap-1">
                    {h.streak}W STREAK
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* empty state */}
        {p.stats.totalSessions === 0 && !p.isReadOnly && (
          <div className="glass-card p-12 text-center col-span-full mt-8">
            <div className="text-6xl mb-6">🃏</div>
            <div className="text-xl font-black text-white uppercase mb-2">No sessions yet</div>
            <div className="text-sm text-neon-muted mb-8 max-w-xs mx-auto">
              Ready to start your first session? Log your wins and losses now.
            </div>
            <Link to="/session/new">
              <button type="button" className="neon-btn-primary flex items-center gap-2 px-8 py-4 mx-auto">
                <Plus className="w-5 h-5" />
                <span className="font-black">START TRACKING</span>
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
